import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { Badge, Button, DataTable, Modal, formatCurrency, formatDateTime, type ColumnDef } from '@lipaflow/ui'
import { adminService, type StaffMember, type TrialPipelineEntry } from '../../services/admin'
import { billingAdjustments } from '../../services/billingApprovals'
import { apiErrorMessage } from '@lipaflow/shared'

const field = 'w-full rounded-button border border-hairline bg-surface p-2.5 text-body-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent'
const labelCls = 'block text-caption text-ink-muted mb-1'

// Status roles only: an expiring trial needs attention, it isn't an error.
function daysBadge(days: number | null) {
  // Null: still in setup; the trial's days start at go-live.
  if (days === null) return <Badge variant="neutral" size="sm">In setup</Badge>
  if (days <= 0) return <Badge variant="neutral" size="sm">Ended</Badge>
  if (days <= 7) return <Badge variant="warning" size="sm">{days} {days === 1 ? 'day' : 'days'} left</Badge>
  return <Badge variant="success" size="sm">{days} days left</Badge>
}

/**
 * Trials — customers trying LipaFlow and who is following them up. One
 * panel per trial holds extend, first invoice, assign and notes. The old
 * card asked staff to type a customer UUID (shown as the placeholder) to
 * confirm, and to paste a staff user ID to assign.
 */
/** A sector's customers on trial. */
export function TrialPipeline({ sector }: { sector: string }) {
  // The id, not a copy: the panel shows the trial as it is after each action.
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const { data: trials, isLoading } = useQuery({
    queryKey: ['trials', sector, 'pipeline'],
    queryFn: () => adminService.getTrialPipeline(sector),
  })
  // assigned_to is a user id; show the person's name.
  const { data: staff = [] } = useQuery({ queryKey: ['admin-staff'], queryFn: () => adminService.listStaff() })
  const who = (id: string | null) => (id ? staff.find((s) => s.id === id)?.full_name ?? 'Someone' : 'Nobody yet')

  if (isLoading || !trials?.length) return null
  const selected = trials.find((t) => t.tenant_id === selectedId) ?? null
  const endingSoon = trials.filter((t) => t.trial_days_remaining !== null && t.trial_days_remaining > 0 && t.trial_days_remaining <= 3).length

  const columns: ColumnDef<TrialPipelineEntry>[] = [
    {
      key: 'who',
      header: 'Customer',
      cell: (t) => (
        <div>
          <div className="font-medium text-ink">{t.tenant_name}</div>
          <div className="text-caption text-ink-muted">{t.admin_email ?? '—'}</div>
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Trial',
      hideOnMobile: true,
      cell: (t) => <span className="text-body-sm text-ink-muted">{t.trial_type === 'demo' ? 'Demo' : t.trial_type === 'institutional' ? 'Full trial' : '—'}</span>,
    },
    { key: 'days', header: 'Time left', cell: (t) => daysBadge(t.trial_days_remaining) },
    { key: 'owner', header: 'Following up', hideOnMobile: true, cell: (t) => <span className="text-body-sm text-ink-muted">{who(t.assigned_to)}</span> },
  ]

  return (
    <section aria-labelledby="trials-heading" className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="trials-heading" className="font-outfit text-h4 text-ink">Trials</h2>
        <p className="text-body-sm text-ink-muted">
          {trials.length} {trials.length === 1 ? 'trial' : 'trials'}
          {endingSoon > 0 && <span className="text-warning font-medium"> · {endingSoon} ending in the next 3 days</span>}
        </p>
      </div>
      <DataTable data={trials} columns={columns} getRowId={(t) => t.tenant_id} onRowClick={(t) => setSelectedId(t.tenant_id)} />
      <TrialPanel trial={selected} staff={staff} onClose={() => setSelectedId(null)} />
    </section>
  )
}

type Mode = 'view' | 'extend' | 'invoice' | 'assign' | 'note'

function TrialPanel({ trial, staff: people, onClose }: { trial: TrialPipelineEntry | null; staff: StaffMember[]; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<Mode>('view')
  const [days, setDays] = useState(14)
  const [reason, setReason] = useState('')
  const [staffId, setStaffId] = useState('')
  const [note, setNote] = useState('')
  const [confirmName, setConfirmName] = useState('')

  const staff = useQuery({ queryKey: ['admin-staff'], queryFn: () => adminService.listStaff() })

  const reset = () => { setMode('view'); setReason(''); setNote(''); setConfirmName(''); setStaffId('') }
  const close = () => { reset(); onClose() }
  const done = (message: string) => { toast.success(message); queryClient.invalidateQueries({ queryKey: ['trials'] }); reset() }

  // Through the extensions engine, so D7 holds: over 14 days waits for a second person.
  const extend = useMutation({
    mutationFn: () => billingAdjustments.requestExtension(trial!.tenant_id, { kind: 'trial', days, reason: reason.trim() }),
    onSuccess: (result) => done(result.status === 'pending_approval'
      ? `Asked: a second person approves ${days} days in Billing approvals`
      : `Trial extended by ${days} days`),
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't extend the trial")),
  })
  const assign = useMutation({
    mutationFn: () => adminService.assignTrialRep(trial!.tenant_id, staffId),
    onSuccess: () => done('Assigned'),
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't assign")),
  })
  const addNote = useMutation({
    mutationFn: () => adminService.addTrialNote(trial!.tenant_id, note.trim()),
    onSuccess: () => done('Note added'),
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't add the note")),
  })

  if (!trial) return <Modal open={false} onClose={close}>{null}</Modal>
  const nameOf = (id: string | null) => (id ? people.find((p) => p.id === id)?.full_name ?? 'Someone' : 'Nobody yet')
  // Changing money or dates for a customer: type their name to confirm it's the right one.
  const confirmed = confirmName.trim().toLowerCase() === trial.tenant_name.trim().toLowerCase()

  const Confirm = (
    <label className="block">
      <span className={labelCls}>Type <span className="text-ink font-medium">{trial.tenant_name}</span> to confirm</span>
      <input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} className={field} autoComplete="off" aria-label="Type the customer's name to confirm" />
    </label>
  )

  return (
    <Modal open onClose={close} title={trial.tenant_name} size="lg">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          {daysBadge(trial.trial_days_remaining)}
          <span className="text-body-sm text-ink-muted">{trial.trial_type === 'demo' ? 'Demo trial' : 'Full trial'}</span>
          {trial.trial_ends_at && <span className="text-body-sm text-ink-muted">· ends {new Date(trial.trial_ends_at).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><dt className="text-caption text-ink-faint">Admin</dt><dd className="text-body-sm text-ink">{trial.admin_email ?? '—'}</dd></div>
          <div><dt className="text-caption text-ink-faint">Following up</dt><dd className="text-body-sm text-ink">{nameOf(trial.assigned_to)}</dd></div>
        </dl>

        {trial.notes.length > 0 && (
          <section aria-labelledby="trial-notes" className="space-y-2">
            <h3 id="trial-notes" className="text-caption text-ink-faint">Notes</h3>
            <ul className="space-y-2">
              {trial.notes.map((n) => (
                <li key={`${n.date}:${n.author}`} className="rounded-button bg-surface-alt px-3 py-2">
                  <p className="text-body-sm text-ink whitespace-pre-line">{n.note}</p>
                  <p className="text-caption text-ink-muted">{nameOf(n.author)} · {formatDateTime(n.date)}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {mode === 'view' && (
          <div className="flex flex-wrap gap-2 pt-4 border-t border-hairline">
            <Button onClick={() => setMode('extend')}>Extend trial</Button>
            <Button variant="secondary" onClick={() => setMode('invoice')}>First invoice</Button>
            <Button variant="ghost" onClick={() => setMode('assign')}>{trial.assigned_to ? 'Reassign' : 'Assign someone'}</Button>
            <Button variant="ghost" onClick={() => setMode('note')}>Add a note</Button>
          </div>
        )}

        {mode === 'extend' && (
          <Section onBack={reset} action={<Button onClick={() => extend.mutate()} disabled={!confirmed || days < 1 || days > 180 || reason.trim().length < 3} isLoading={extend.isPending}>Extend by {days} days</Button>}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block"><span className={labelCls}>Extra days</span>
                <input type="number" min={1} max={180} value={days} onChange={(e) => setDays(Number(e.target.value))} className={field} />
              </label>
              <label className="block"><span className={labelCls}>Why</span>
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Asked for more time before term starts" className={field} />
              </label>
            </div>
            <p className="text-caption text-ink-muted">Up to 14 days applies at once; longer waits for a second person in Billing approvals.</p>
            {Confirm}
          </Section>
        )}

        {mode === 'invoice' && (
          <FirstInvoice tenantId={trial.tenant_id} confirm={Confirm} confirmed={confirmed} onBack={reset} onRaised={done} />
        )}

        {mode === 'assign' && (
          <Section onBack={reset} action={<Button onClick={() => assign.mutate()} disabled={!staffId} isLoading={assign.isPending}>Assign</Button>}>
            <label className="block"><span className={labelCls}>Who should follow up?</span>
              <select value={staffId} onChange={(e) => setStaffId(e.target.value)} className={field} aria-label="Staff member">
                <option value="">{staff.isLoading ? 'Loading…' : 'Choose…'}</option>
                {(staff.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.full_name} · {s.email}</option>)}
              </select>
            </label>
          </Section>
        )}

        {mode === 'note' && (
          <Section onBack={reset} action={<Button onClick={() => addNote.mutate()} disabled={!note.trim()} isLoading={addNote.isPending}>Add note</Button>}>
            <label className="block"><span className={labelCls}>Note</span>
              <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Spoke with the bursar; wants to start in Term 2" className={field} />
            </label>
          </Section>
        )}
      </div>
    </Modal>
  )
}

function Section({ children, action, onBack }: { children: React.ReactNode; action: React.ReactNode; onBack: () => void }) {
  return (
    <div className="pt-4 border-t border-hairline space-y-3">
      {children}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onBack}>Back</Button>
        {action}
      </div>
    </div>
  )
}

/**
 * The first invoice: what the customer will be billed, worked out from what
 * they use (the price book's band for the count, a contract rate, vouchers),
 * the same quote the customer sees. Staff don't pick a plan or type a count;
 * the one choice is how they pay (each term or month, or a year up front),
 * and a discount is a code applied to the customer, which the quote shows.
 */
const PERIOD: Record<string, string> = { termly: 'term', monthly: 'month' }
const day = (iso: string) => new Date(iso).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })

function FirstInvoice({ tenantId, confirm, confirmed, onBack, onRaised }: {
  tenantId: string
  confirm: React.ReactNode
  confirmed: boolean
  onBack: () => void
  onRaised: (message: string) => void
}) {
  const queryClient = useQueryClient()
  const [code, setCode] = useState('')
  const [chosen, setChosen] = useState<string | null>(null)
  const current = useQuery({ queryKey: ['first-invoice', tenantId, 'base'], queryFn: () => adminService.getFirstInvoice(tenantId) })
  const invoice = current.data?.invoice ?? null
  const baseCycle = current.data?.quote.base_cycle
  const cycle = chosen ?? invoice?.cycle ?? baseCycle ?? null
  const preview = useQuery({
    queryKey: ['first-invoice', tenantId, cycle],
    queryFn: () => adminService.getFirstInvoice(tenantId, cycle ?? undefined),
    enabled: Boolean(cycle),
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['first-invoice', tenantId] })
  const voucher = useMutation({
    mutationFn: () => adminService.applyVoucher(tenantId, code.trim()),
    onSuccess: () => { toast.success('Code applied'); setCode(''); refresh() },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't apply that code")),
  })
  const raise = useMutation({
    mutationFn: () => adminService.raiseFirstInvoice(tenantId, cycle ?? undefined),
    onSuccess: (data) => {
      refresh()
      onRaised(invoice ? `Invoice ${data.invoice.number} replaces ${invoice.number}` : `Invoice ${data.invoice.number} raised`)
    },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't raise the invoice")),
  })

  if (current.isLoading || (cycle && preview.isLoading)) {
    return <Section onBack={onBack} action={null}><p role="status" className="text-body-sm text-ink-muted">Working out the invoice…</p></Section>
  }
  const failed = current.isError ? current.error : preview.isError ? preview.error : null
  if (failed || !preview.data) {
    return (
      <Section onBack={onBack} action={null}>
        <p role="alert" className="text-body-sm text-warning">{apiErrorMessage(failed, "Couldn't work out their invoice.")}</p>
      </Section>
    )
  }
  const q = preview.data.quote
  const money = (n: number) => formatCurrency(n, true, q.currency)
  const noun = PERIOD[q.base_cycle] ?? 'period'
  const label = (c: string | null) => (c === 'yearly' ? 'a year up front' : `each ${noun}`)
  // An invoice already raised is changed only to another cycle, and only while nothing is paid on it.
  const switching = Boolean(invoice && invoice.cycle !== cycle)
  const locked = Boolean(invoice && (invoice.paid_amount > 0 || !switching))

  const nothing = q.count === 0
  return (
    <Section
      onBack={onBack}
      action={locked ? null : (
        <Button onClick={() => raise.mutate()} disabled={!confirmed || nothing || q.needs_contract} isLoading={raise.isPending}>
          {switching ? `Change it to ${label(cycle)}` : 'Raise the invoice now'}
        </Button>
      )}
    >
      {invoice && (
        <p className="text-body-sm text-ink">
          Invoice <span className="font-mono">{invoice.number}</span> for {money(invoice.amount)}, paying {label(invoice.cycle)}, is raised
          {invoice.paid_amount > 0 && <>, {money(invoice.paid_amount)} paid</>}
          {invoice.due_date && <>, due {day(invoice.due_date)}</>}. The trial ends when it is paid.
          {invoice.paid_amount > 0 && q.cycles.length > 1 && ' Part of it is paid, so how they pay can no longer change.'}
        </p>
      )}

      {q.cycles.length > 1 && !(invoice && invoice.paid_amount > 0) && (
        <fieldset>
          <legend className={labelCls}>How they pay</legend>
          <div className="flex flex-wrap gap-2">
            {q.cycles.map((c) => (
              <label key={c} className={clsx('flex items-center gap-2 rounded-button border px-3 py-2 text-body-sm cursor-pointer', cycle === c ? 'border-accent text-ink' : 'border-hairline text-ink-muted')}>
                <input type="radio" name={`cycle-${tenantId}`} value={c} checked={cycle === c} onChange={() => setChosen(c)} />
                {c === 'yearly' ? 'A year up front' : `Each ${noun}`}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {nothing ? (
        <p className="text-body-sm text-ink-muted">Nothing to bill yet: they have no {q.unit_label} on LipaFlow.</p>
      ) : q.needs_contract ? (
        <p className="text-body-sm text-warning">
          {q.count.toLocaleString('en-KE')} {q.unit_label} is above every price band. Agree a contract rate before invoicing.
        </p>
      ) : (!invoice || switching) && (
        <div className="rounded-card border border-hairline p-4 space-y-2">
          <p className="text-body-sm text-ink-muted">
            {q.count.toLocaleString('en-KE')} {q.unit_label}
            {q.contract ? ' at their contract rate' : q.band ? ` · ${q.band} band` : ''}
            {' · '}{day(q.period_start)} to {day(q.period_end)}
          </p>
          <ul className="space-y-1">
            {q.lines.map((line, i) => (
              <li key={i} className="flex justify-between gap-3 text-body-sm">
                <span className="text-ink">{line.description}</span>
                <span className="tabular text-ink">{money(line.amount)}</span>
              </li>
            ))}
          </ul>
          <p className="flex justify-between gap-3 border-t border-hairline pt-2 font-medium text-ink">
            <span>Total</span><span className="tabular">{money(q.amount ?? 0)}</span>
          </p>
        </div>
      )}

      {!locked && (
        <>
          {!invoice && (
            <div className="flex items-end gap-2">
              <label className="block flex-1"><span className={labelCls}>Discount code (optional)</span>
                <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className={clsx(field, 'font-mono')} />
              </label>
              <Button variant="secondary" onClick={() => voucher.mutate()} disabled={!code.trim()} isLoading={voucher.isPending}>Apply</Button>
            </div>
          )}
          <p className="text-caption text-ink-muted">
            {switching
              ? `Replaces ${invoice!.number}; nothing has been paid on it. The trial keeps running until the new one is paid.`
              : 'The trial keeps running until the invoice is paid. They have 7 days to pay.'}
          </p>
          {confirm}
        </>
      )}
    </Section>
  )
}
